import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { CryptoInput } from '@swyftx/aviary/molecules/CryptoInput';
import { FiatInput } from '@swyftx/aviary/molecules/FiatInput/FiatInput';

import { Big } from '@shared/safe-big';
import { RatesStore } from '@shared/store';
import { formatCurrency } from '@shared/utils';

import { useCurrentPrice } from '@hooks/Trade/useCurrentPrice';
import {
  EstimatedLoanSummary,
  estimateLoanSummary,
  MAX_ESTIMATED_LOAN_AMOUNT,
  MAX_LVR,
  MIN_ESTIMATED_LOAN_AMOUNT,
} from '@utils/calculator';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';
import { usePortfolioBalance } from 'src/lib/portfolio/hooks/usePortfolioBalance';

type Props = {
  className?: string;
  onClick?: () => void;
  hasError: boolean;
};
const MAX_INPUT_LIMIT = 1_000_000_000_000;

const LoanInfoRow = ({ label, value, isLoading = false }: { label: string; value: string; isLoading?: boolean }) => (
  <FlexLayout direction='row' justifyContent='space-between' className='w-full'>
    <Body size='medium' color='inverse'>
      {label}
    </Body>
    <Numeric size='medium' weight='bold' color='inverse'>
      {isLoading ? '—' : value}
    </Numeric>
  </FlexLayout>
);

const CryptoLoanCalculator: React.FC<Props> = ({ className, onClick, hasError }) => {
  const { t } = useTranslation('loans');

  const [loanAmount, setLoanAmount] = useState<string>('');
  const [securityAmount, setSecurityAmount] = useState<string>('');
  const [loanError, setLoanError] = useState<string | undefined>(undefined);
  const [securityError, setSecurityError] = useState<string | undefined>(undefined);
  const [availableBalance, setAvailableBalance] = useState<string | undefined>('0');
  const { convertRate } = RatesStore.useRatesStore;
  const { navigate } = useNavigateRoute();

  const { getAssetByCode } = useMarkets();
  const { getBalance } = usePortfolioBalance();

  const [loanSummary, setLoanSummary] = useState<EstimatedLoanSummary | undefined>(undefined);

  // this can be updated when we add new assets to our loan product
  const BTC = getAssetByCode('BTC');
  const AUD = getAssetByCode('AUD');

  const currentPrice = Big(useCurrentPrice(AUD, BTC, 'midPrice'));

  const setAmounts = (loan: string, security: string) => {
    if (!loan || !security) {
      setLoanAmount('');
      setSecurityAmount('');
      return;
    }
    if (Number(loan) > MAX_INPUT_LIMIT) {
      return;
    }
    setLoanAmount(loan);
    setSecurityAmount(security);
  };

  const handleLoanAmountChange = (value: string) => {
    if (!(BTC && AUD)) {
      return;
    }
    const btcEquivalent = convertRate(AUD, BTC, value, 'midPrice');
    setAmounts(value, btcEquivalent.div(MAX_LVR).toFixed(8));
  };

  const handleSecurityAmountChange = (value: string) => {
    if (!(BTC && AUD)) {
      return;
    }

    const audEquivalent = convertRate(BTC, AUD, value, 'midPrice');
    setAmounts(audEquivalent.times(MAX_LVR).toFixed(2), value);
  };

  const setMaxAmount = (type: 'FIAT' | 'CRYPTO') => {
    if (!BTC || !AUD) {
      return;
    }

    const userBtcBalance = Big(getBalance(BTC.id)?.availableBalance ?? '0');
    const maxEstimatedLoanAmountBig = Big(MAX_ESTIMATED_LOAN_AMOUNT);

    if (type === 'FIAT') {
      const maxLoanableAudFromUserBtc = userBtcBalance.times(currentPrice).times(MAX_LVR);
      let audAmountToSet: Big;
      if (maxLoanableAudFromUserBtc.gt(maxEstimatedLoanAmountBig)) {
        audAmountToSet = maxEstimatedLoanAmountBig;
      } else {
        audAmountToSet = maxLoanableAudFromUserBtc;
      }

      if (audAmountToSet.lt(0)) {
        audAmountToSet = Big(0);
      }

      const finalAudAmountString = audAmountToSet.toFixed(2);

      handleLoanAmountChange(finalAudAmountString);
    } else if (type === 'CRYPTO') {
      handleSecurityAmountChange(userBtcBalance.toFixed(8));
    }
  };

  const formatInfoCurrencyRow = (amount: string | undefined) => (amount ? `${formatCurrency(amount, AUD)}` : '-');

  useEffect(() => {
    if (BTC) {
      setAvailableBalance(getBalance(BTC.id)?.availableBalance);
    }
    if (loanAmount || securityAmount) {
      setLoanSummary(estimateLoanSummary({ fiat: loanAmount, rate: currentPrice.toFixed(2) }));
    } else {
      setLoanSummary(undefined);
    }
  }, [BTC, loanAmount, securityAmount, getBalance, currentPrice]);

  useEffect(() => {
    if (!(AUD && BTC)) {
      return;
    }
    if (!(loanAmount || securityAmount)) {
      return;
    }

    if (Number(loanAmount) > MAX_ESTIMATED_LOAN_AMOUNT || Number(loanAmount) < MIN_ESTIMATED_LOAN_AMOUNT) {
      setLoanError(
        t('calculator.inputs.loan.rangeError', {
          min: formatCurrency(MIN_ESTIMATED_LOAN_AMOUNT, AUD),
          max: formatCurrency(MAX_ESTIMATED_LOAN_AMOUNT, AUD),
        }),
      );
      setSecurityError(
        t('calculator.inputs.security.rangeError', {
          max: convertRate(AUD, BTC, MAX_ESTIMATED_LOAN_AMOUNT.toString(), 'midPrice').toString(),
          min: convertRate(AUD, BTC, MIN_ESTIMATED_LOAN_AMOUNT.toString(), 'midPrice').toString(),
          asset: 'BTC',
        }),
      );
      return;
    }
    setLoanError(undefined);
    setSecurityError(undefined);
  }, [AUD, BTC, loanAmount, securityAmount, convertRate, t]);

  return (
    <FlexLayout
      id='loan-calculator'
      onClick={onClick}
      className={`min-h-[510px] overflow-hidden rounded-16 ${className} border-color-border-default flex-col border md:flex-row`}
    >
      {/* Calculator */}
      <FlexLayout
        direction='column'
        className='w:full min-h-[470px] flex-shrink-0 bg-color-background-surface-primary bg-cover bg-center p-24 md:min-h-[400px] md:w-1/2'
        spacing={24}
      >
        <FlexLayout direction='column' spacing={8}>
          <Body size='large' weight='none' color='accent'>
            {t('calculator.title')}
          </Body>
          <Heading size='h2' color='primary' weight='light' className='mt-4'>
            {t('calculator.description')}
          </Heading>
        </FlexLayout>
        <FlexLayout direction='column' spacing={8}>
          <FlexLayout direction='column' spacing={4}>
            <Body size='small' weight='emphasis' color='secondary'>
              {t('calculator.inputs.loan.label')}
            </Body>
            <Body size='small' color='secondary'>
              {t('calculator.inputs.loan.description', {
                min: formatCurrency(MIN_ESTIMATED_LOAN_AMOUNT.toString(), AUD),
                max: formatCurrency(MAX_ESTIMATED_LOAN_AMOUNT.toString(), AUD),
                AUD,
              })}
            </Body>
          </FlexLayout>

          <FiatInput
            onChange={handleLoanAmountChange}
            asset={AUD}
            onMax={() => setMaxAmount('FIAT')}
            placeholder='$0.00'
            value={loanAmount}
            errorMessage={loanError}
          />

          <FlexLayout direction='column' spacing={4} className='mt-24'>
            <Body size='small' weight='none'>
              {t('calculator.inputs.security.label')}
            </Body>
            <Body size='small' weight='none' color='secondary'>
              {`Available: ${formatCurrency(availableBalance, BTC, { appendCode: true })}`}
            </Body>
          </FlexLayout>

          <CryptoInput
            onChange={handleSecurityAmountChange}
            fiatEquivalent={AUD && BTC ? convertRate(BTC, AUD, securityAmount, 'midPrice').toString() : '0'}
            asset={BTC}
            onMax={() => setMaxAmount('CRYPTO')}
            placeholder='0.0000'
            value={securityAmount}
            errorMessage={securityError}
          />
        </FlexLayout>
      </FlexLayout>

      {/*  Information */}
      <FlexLayout
        direction='column'
        spacing={16}
        className='border-color-border-subtle flex min-h-[510px] flex-1 justify-between border-l bg-color-background-secondary bg-cover p-32'
      >
        <FlexLayout direction='column'>
          <Heading size='h5' color='inverse'>
            {t('calculator.summary.title')}
          </Heading>
          <div className='mt-8'>
            <FlexLayout direction='column' spacing={12}>
              <LoanInfoRow
                label={t('calculator.summary.amount')}
                value={formatInfoCurrencyRow(loanSummary?.loanAmount)}
              />
              <LoanInfoRow
                label={t('calculator.summary.fee')}
                value={formatInfoCurrencyRow(loanSummary?.originationFee)}
              />
              <LoanInfoRow
                label={t('calculator.summary.interest')}
                value={formatInfoCurrencyRow(loanSummary?.maxInterest)}
              />
              <LoanInfoRow
                label={t('calculator.summary.repayment')}
                value={formatInfoCurrencyRow(loanSummary?.totalRepayment)}
              />
              <LoanInfoRow
                label={t('calculator.summary.security')}
                value={`${loanSummary?.securityRequired ?? '-'} BTC`}
              />
              <LoanInfoRow
                label={t('calculator.summary.value')}
                value={`${loanSummary?.securityInFiat ? '~' : ''}${formatInfoCurrencyRow(loanSummary?.securityInFiat)}`}
              />
            </FlexLayout>
          </div>

          <Utility variant='caption' className='mt-24 whitespace-pre-wrap' color='inverse'>
            {t('calculator.summary.disclaimer')}
          </Utility>
        </FlexLayout>

        {loanAmount && (
          <FlexLayout direction='column'>
            <Heading size='h5' color='inverse'>
              {t('calculator.actions.title')}
            </Heading>
            <Body size='medium' color='inverse'>
              {t('calculator.actions.description')}
            </Body>
            <Button
              className='mt-12'
              size='lg'
              disabled={loanError !== undefined || hasError}
              onClick={() => navigate(NavigationURLs.LoansApplication)}
            >
              {t('calculator.actions.button')}
            </Button>
          </FlexLayout>
        )}
      </FlexLayout>
    </FlexLayout>
  );
};

CryptoLoanCalculator.displayName = 'CryptoLoanCalculator';

export { CryptoLoanCalculator };
